// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { MealInstance } from "@bitebalance/contracts"

/**
 * T21 (ADR-070) — a meal is marked by its TIME OF DAY, never by its food.
 *
 * Sunrise, high sun, low sun, moon. The mark encodes the slot the server
 * already sends, so it can never make a claim the data cannot back, and it
 * gives a day a rhythm that four identical text blocks do not. The web twin of
 * `mobile/src/ui/daylight.tsx`; the two clients draw the same four marks so the
 * product reads as one family.
 */
export function SlotGlyph({ slot, className }: { slot: MealInstance["slot"]; className?: string }) {
  const stroke = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className ?? "size-4 text-green-2"}>
      {slot === "breakfast" ? (
        <>
          <path d="M3 18h18" {...stroke} />
          <path d="M12 8.5a5 5 0 0 1 5 5H7a5 5 0 0 1 5-5z" {...stroke} />
          <path d="M12 3v2M5.2 6.2l1.4 1.4M18.8 6.2l-1.4 1.4" {...stroke} />
        </>
      ) : slot === "lunch" ? (
        <>
          <circle cx={12} cy={12} r={4} {...stroke} />
          <path
            d="M12 3v2M12 19v2M3 12h2M19 12h2M6 6l1.4 1.4M18 6l-1.4 1.4M6 18l1.4-1.4M18 18l-1.4-1.4"
            {...stroke}
          />
        </>
      ) : slot === "morning_snack" || slot === "afternoon_snack" ? (
        <>
          <path d="M3 15.5h18" {...stroke} />
          <circle cx={12} cy={11} r={3.4} {...stroke} />
          <path d="M12 4v1.8M6.4 8.2l1.3 1.3M17.6 8.2l-1.3 1.3" {...stroke} />
        </>
      ) : (
        <path d="M17.5 14.6A6.5 6.5 0 0 1 9.4 6.5a6.5 6.5 0 1 0 8.1 8.1z" {...stroke} />
      )}
    </svg>
  )
}

/** The clock, not the food — the same three phases the mobile ground uses. */
export function dayPhaseClass(hour = new Date().getHours()): string {
  if (hour < 11) return "bb-ground-dawn"
  if (hour < 18) return "bb-ground-noon"
  return "bb-ground-dusk"
}
